import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { actionButtonVariant, type ActionSpec } from '../actions/specs';

/**
 * Action panel (doc 09 §4.6). Renders the variant-driven ActionSpec[] grouped by `group`. Every button
 * is disabled while any mutation is pending to block double-submit on irreversible ops. The confirm /
 * toast / undo policy is owned by the action callbacks (useModerationActions).
 */
export function ActionPanel({ specs, disabled }: { specs: ActionSpec[]; disabled: boolean }) {
  if (specs.length === 0) return null;

  // Preserve group order of first appearance.
  const groups = new Map<string, ActionSpec[]>();
  for (const spec of specs) {
    const list = groups.get(spec.group) ?? [];
    list.push(spec);
    groups.set(spec.group, list);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {[...groups.entries()].map(([group, items]) => (
          <div key={group} className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">{group}</p>
            <div className="flex flex-wrap gap-2">
              {items.map((spec) => (
                <Button
                  key={spec.id}
                  size="sm"
                  variant={actionButtonVariant(spec.variant)}
                  disabled={disabled}
                  onClick={() => void spec.run()}
                >
                  {spec.label}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
